import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Key, 
  Gauge, 
  Bell, 
  FileText, 
  Settings,
  LogOut,
  ChevronLeft
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/atoms/Logo';
import { useAuthStore } from '@/store/authStore';
import { useShallow } from 'zustand/react/shallow';

interface AdminLayoutProps {
  children: ReactNode;
}

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { id: 'clients', label: 'Clientes', icon: Users, path: '/admin/clients' },
  { id: 'subscriptions', label: 'Assinaturas', icon: CreditCard, path: '/admin/subscriptions' },
  { id: 'api-keys', label: 'API Keys', icon: Key, path: '/admin/api-keys' },
  { id: 'limits', label: 'Limites', icon: Gauge, path: '/admin/limits' },
  { id: 'alerts', label: 'Alertas', icon: Bell, path: '/admin/alerts' },
  { id: 'logs', label: 'Logs', icon: FileText, path: '/admin/logs' },
  { id: 'settings', label: 'Configurações', icon: Settings, path: '/admin/settings' },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthStore(useShallow((state) => ({ signOut: state.signOut })));

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const currentItem = menuItems.find(item => 
    location.pathname === item.path || 
    (item.path !== '/admin' && location.pathname.startsWith(item.path))
  ) || menuItems[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Admin Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full z-50 flex flex-col",
          "bg-red-950/20 backdrop-blur-2xl border-r border-red-500/20",
          "transition-all duration-500 ease-out",
          isExpanded ? "w-64" : "w-20"
        )}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-center border-b border-red-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/25">
              <span className="text-white font-bold text-sm">ADM</span>
            </div>
            <span className={cn(
              "text-lg font-bold text-red-400 whitespace-nowrap transition-all duration-300",
              isExpanded ? "opacity-100 w-auto" : "opacity-0 w-0 overflow-hidden"
            )}>
              ADMIN
            </span>
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="px-3 py-4 border-b border-red-500/10">
          <button
            onClick={() => navigate('/dashboard')}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-xl",
              "text-muted-foreground hover:text-foreground hover:bg-white/5",
              "transition-all duration-200"
            )}
          >
            <ChevronLeft size={20} />
            <span className={cn(
              "whitespace-nowrap transition-all duration-300",
              isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            )}>
              Voltar ao App
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl relative group",
                  "transition-all duration-200",
                  isActive
                    ? "bg-red-500/20 text-red-400"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                )}
              >
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-red-500 to-orange-500 rounded-r-full" />
                )}
                <Icon size={20} className={cn(isActive && "text-red-400")} />
                <span className={cn(
                  "whitespace-nowrap transition-all duration-300",
                  isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
                )}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-red-500/10">
          <button
            onClick={handleLogout}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl",
              "text-red-400 hover:bg-red-500/10",
              "transition-all duration-200"
            )}
          >
            <LogOut size={20} />
            <span className={cn(
              "whitespace-nowrap transition-all duration-300",
              isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            )}>
              Sair
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={cn(
        "min-h-screen transition-all duration-500",
        isExpanded ? "pl-64" : "pl-20"
      )}>
        {/* Header */}
        <header className="h-20 border-b border-white/[0.06] bg-white/[0.02] backdrop-blur-xl sticky top-0 z-40">
          <div className="h-full px-8 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {currentItem.label}
              </h1>
              <p className="text-sm text-muted-foreground">
                Painel Administrativo INNFLOW
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20">
                <span className="text-xs font-medium text-red-400">Super Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
